import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CloudFrontSigner } from '../../../../utils/cloud-front-signer/cloud-front-signer';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { GeodataSetupContextType } from '../../../state/geodata-setup-state';
import { useUploadImageQueue } from '../geodata-image-list/use-image-upload-queue';
import { useGeodataCloudFrontSigner } from '../use-geodata-cloudfront-signer';
import { UploadStepStatus } from './use-upload-boundary-polygons-step';

type ImageFile = {
  filename: string;
  file: File;
  imageIdentifier: string;
};

type UploadStatus = 'idle' | 'loading' | 'success' | 'error';

export type UseImageUploadQueueStateResult = {
  status: UploadStatus;
  completedCount: number;
  files: string[];
  totalCount: number;
  currentIndex: number;
  failedCount: number;
  error: Error | null;
  startUpload: () => void;
};

export const useUploadImagesStep = (
  context: GeodataSetupContextType | undefined,
  geodataId: string | undefined,
  boundaryStepStatus: UploadStepStatus,
) => {
  const signer = useGeodataCloudFrontSigner(geodataId || '');
  const { buildErrorList, handleError, clearErrors } = useErrorHandling({
    displayNonApiErrors: false,
  });
  const uploadInitiatedRef = useRef(false);

  const imageFiles = context?.imageFiles || [];
  const imageUploadQueue = useImageUploadQueueState(imageFiles, signer);

  const retry = useCallback(() => {
    uploadInitiatedRef.current = false;
    imageUploadQueue.startUpload();
  }, [imageUploadQueue]);

  const errorList = useMemo(() => {
    return buildErrorList({ retryCallback: retry });
  }, [buildErrorList, retry]);

  // Map hook status to StepStatus
  const uploadStepStatus = useMemo<UploadStepStatus>(() => {
    if (imageUploadQueue.status === 'idle' && !imageFiles.length) {
      return 'success';
    }
    if (imageUploadQueue.status === 'idle') {
      return 'pending';
    }
    if (imageUploadQueue.status === 'loading') {
      return 'loading';
    }
    if (imageUploadQueue.status === 'success' && imageUploadQueue.failedCount === 0) {
      return 'success';
    }
    if (imageUploadQueue.status === 'success' || imageUploadQueue.status === 'error') {
      return 'error';
    }
    return 'error';
  }, [imageUploadQueue.status, imageUploadQueue.failedCount, imageFiles.length]);

  // Calculate progress from completed count
  const progress = useMemo(() => {
    return imageUploadQueue.totalCount > 0
      ? Math.floor((100 * imageUploadQueue.completedCount) / imageUploadQueue.totalCount)
      : 0;
  }, [imageUploadQueue.totalCount, imageUploadQueue.completedCount]);

  // Handle errors from the upload queue
  useEffect(() => {
    if (imageUploadQueue.error) {
      handleError(imageUploadQueue.error);
    } else if (imageUploadQueue.status === 'success' && imageUploadQueue.failedCount === 0) {
      clearErrors();
    }
  }, [
    imageUploadQueue.error,
    imageUploadQueue.status,
    imageUploadQueue.failedCount,
    handleError,
    clearErrors,
  ]);

  // Auto-start upload when conditions are met
  useEffect(() => {
    if (!geodataId || !context || !signer) {
      return;
    }

    // Prevent duplicate upload initiation in React Strict Mode
    if (uploadInitiatedRef.current || imageUploadQueue.status !== 'idle') {
      return;
    }

    // Step 3: Upload images (only if boundary step succeeded and there are files)
    if (boundaryStepStatus === 'success') {
      if (imageFiles.length > 0) {
        uploadInitiatedRef.current = true;
        imageUploadQueue.startUpload();
      }
      // If no image files, the status will remain 'idle' which maps to 'success' in the component
    }
  }, [geodataId, context, signer, boundaryStepStatus, imageFiles.length, imageUploadQueue]);

  // Reset upload initiation when geodataId or signer changes
  useEffect(() => {
    uploadInitiatedRef.current = false;
  }, [geodataId, signer]);

  return {
    imageUploadQueue,
    uploadStepStatus,
    progress,
    errorList,
    retry,
  };
};

function useImageUploadQueueState(
  imageFiles: ImageFile[],
  signer: CloudFrontSigner | undefined,
): UseImageUploadQueueStateResult {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [completedCount, setCompletedCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);
  const successfulIndicesRef = useRef<Set<number>>(new Set());
  const failedIndicesRef = useRef<Set<number>>(new Set());

  const totalCount = imageFiles.length;
  const queueUpload = useUploadImageQueue();

  // Calculate the highest consecutive successful index starting from 1
  const calculateCurrentIndex = useCallback(
    (successfulIndices: Set<number>): number => {
      let highestConsecutive = 0;
      for (let i = 1; i <= totalCount; i++) {
        if (successfulIndices.has(i)) {
          highestConsecutive = i;
        } else {
          break;
        }
      }
      return highestConsecutive;
    },
    [totalCount],
  );

  // Helper function to check completion and update status
  const checkCompletionAndUpdateStatus = useCallback(() => {
    if (cancelledRef.current) {
      return;
    }

    const successfulCount = successfulIndicesRef.current.size;
    const failedCount = failedIndicesRef.current.size;
    const totalProcessed = successfulCount + failedCount;
    const currentIndex = calculateCurrentIndex(successfulIndicesRef.current);

    setCompletedCount(successfulCount);
    setFailedCount(failedCount);
    setCurrentIndex(currentIndex);

    // Check if all uploads are complete (successful + failed)
    if (totalProcessed >= totalCount) {
      if (failedCount >= totalCount) {
        setError(new Error('All images failed to upload'));
        setStatus('error');
      } else if (failedCount > 0) {
        setError(new Error('One or more images failed to upload'));
        setStatus('error');
      } else {
        setStatus('success');
      }
    }
  }, [totalCount, calculateCurrentIndex]);

  const startUpload = useCallback(() => {
    if (!signer || !imageFiles.length || status === 'loading') {
      return;
    }

    // Check if we're retrying failed uploads or starting fresh
    const hasFailedUploads = failedIndicesRef.current.size > 0;
    const isRetry = hasFailedUploads;

    setStatus('loading');
    setError(null);
    cancelledRef.current = false;

    if (isRetry) {
      // Retry only failed uploads - preserve successful ones
      const failedIndices = Array.from(failedIndicesRef.current);
      failedIndicesRef.current = new Set();

      try {
        // Queue only failed uploads for retry
        failedIndices.forEach((index) => {
          const imageIndex = index - 1; // Convert to 0-based index
          const { file, imageIdentifier } = imageFiles[imageIndex];
          void queueUpload({
            imageIdentifier,
            file,
            signer: signer,
            onSuccess: () => {
              if (!cancelledRef.current) {
                successfulIndicesRef.current.add(index);
                failedIndicesRef.current.delete(index);
                checkCompletionAndUpdateStatus();
              }
            },
            onError: () => {
              if (!cancelledRef.current) {
                failedIndicesRef.current.add(index);
                checkCompletionAndUpdateStatus();
              }
            },
          });
        });
      } catch (err) {
        if (!cancelledRef.current) {
          console.error('Failed to queue image uploads:', err);
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setStatus('error');
        }
      }
    } else {
      // Start fresh - reset everything
      setCompletedCount(0);
      setCurrentIndex(0);
      setFailedCount(0);
      successfulIndicesRef.current = new Set();
      failedIndicesRef.current = new Set();

      try {
        // Queue all uploads
        imageFiles.forEach(({ file, imageIdentifier }, i) => {
          const index = i + 1;
          void queueUpload({
            imageIdentifier,
            file,
            signer: signer,
            onSuccess: () => {
              if (!cancelledRef.current) {
                successfulIndicesRef.current.add(index);
                checkCompletionAndUpdateStatus();
              }
            },
            onError: () => {
              if (!cancelledRef.current) {
                failedIndicesRef.current.add(index);
                checkCompletionAndUpdateStatus();
              }
            },
          });
        });
      } catch (err) {
        if (!cancelledRef.current) {
          console.error('Failed to queue image uploads:', err);
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setStatus('error');
        }
      }
    }
  }, [imageFiles, signer, status, queueUpload, checkCompletionAndUpdateStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const files = useMemo(() => {
    return imageFiles.map(({ file }) => file.name);
  }, [imageFiles]);

  return {
    status,
    completedCount,
    files,
    totalCount,
    currentIndex,
    failedCount,
    error,
    startUpload,
  };
}
